import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class UpdateTemplateFileParams {
  @IsUUID()
  @IsNotEmpty()
  template_id: string;

  @IsUUID()
  @IsNotEmpty()
  file_id: string;
}

export class PreviewTemplateParams {
  @IsUUID()
  @IsNotEmpty()
  template_id: string;
}

export class DownloadTemplateFileParams {
  @IsUUID()
  @IsNotEmpty()
  template_id: string;
}

export class UploadTemplateFileParams {
  @IsUUID()
  @IsNotEmpty()
  template_id: string;
}

export class GetTemplateParams {
  @IsUUID()
  @IsNotEmpty()
  template_id: string;
}

export class GetTemplateHistoryParams {
  @IsUUID()
  @IsNotEmpty()
  template_id: string;
}

export type FileId = string;

export class DownloadTemplateFileQuery {
  @IsUUID()
  @IsOptional()
  file_id?: string;
}

export class FileResponse {
  @Type(() => String)
  @IsNotEmpty()
  file: File;
}

export type PlaceholderKey = string;

export class TemplatePlaceholder {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsString()
  @IsOptional()
  value?: string;

  @IsIn(['text', 'date'])
  @IsString()
  @IsNotEmpty()
  type: 'text' | 'date';

  @IsBoolean()
  @IsNotEmpty()
  is_generic: boolean;

  @IsBoolean()
  @IsNotEmpty()
  in_file: boolean;
}

export class UploadTemplateResponse {
  @IsUUID()
  @IsNotEmpty()
  file_id: string;

  @ValidateNested({ each: true })
  @Type(() => TemplatePlaceholder)
  @IsArray()
  @IsNotEmpty()
  placeholders: TemplatePlaceholder[];

  @IsString()
  @IsOptional()
  alert?: string;
}

export class TimeRange {
  @IsDateString()
  @IsOptional()
  start?: string;

  @IsDateString()
  @IsOptional()
  end?: string;
}

export class EventTypeItemChild_button {
  @IsBoolean()
  @IsNotEmpty()
  is_active: boolean;

  @ValidateNested({ each: true })
  @Type(() => TimeRange)
  @IsArray()
  @IsNotEmpty()
  active_periods: TimeRange[];
}

export type EventCategory = string;

export class EventTypeItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['item'])
  @IsString()
  @IsNotEmpty()
  type: 'item';

  @IsString()
  @IsNotEmpty()
  custom_type: string;

  @IsIn(['employee', 'candidate', 'vacation'])
  @IsString()
  @IsOptional()
  category?: 'employee' | 'candidate' | 'vacation';

  @IsBoolean()
  @IsOptional()
  document_info_required?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  disabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  web_editable: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @ValidateNested()
  @Type(() => EventTypeItemChild_button)
  @IsOptional()
  child_button?: EventTypeItemChild_button;
}

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class InfoTemplateResponse {
  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => EventTypeItem)
  @IsArray()
  @IsNotEmpty()
  event_types: EventTypeItem[];

  @ValidateNested({ each: true })
  @Type(() => TemplatePlaceholder)
  @IsArray()
  @IsNotEmpty()
  placeholders: TemplatePlaceholder[];
}

export class TemplateHistory {
  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  file_name: string;

  @IsUUID()
  @IsNotEmpty()
  file_id: string;

  @IsString()
  @IsNotEmpty()
  uploaded_by: string;

  @IsBoolean()
  @IsOptional()
  is_current?: boolean;
}

export class TemplateHistoryResponse {
  @ValidateNested({ each: true })
  @Type(() => TemplateHistory)
  @IsArray()
  @IsNotEmpty()
  history: TemplateHistory[];
}

export class Template {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => EventTypeItem)
  @IsArray()
  @IsNotEmpty()
  event_types: EventTypeItem[];
}

export class TemplatesListResponse {
  @ValidateNested({ each: true })
  @Type(() => Template)
  @IsArray()
  @IsNotEmpty()
  templates: Template[];
}

export class PlaceholderValue {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  value: string;
}

export class PreviewTemplateRequest {
  @IsUUID()
  @IsOptional()
  file_id?: string;

  @ValidateNested({ each: true })
  @Type(() => PlaceholderValue)
  @IsArray()
  @IsNotEmpty()
  items: PlaceholderValue[];
}

export class UploadTemplateRequest {
  @Type(() => String)
  @IsNotEmpty()
  file: File;
}

export class CompanyTemplatesRequest {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

@injectable()
export class TemplatesService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async updateTemplateFile({ params }: { params: UpdateTemplateFileParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateTemplateFileParams),
    ]);

    let requestPath = '/template/{template_id}/{file_id}/accept';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateTemplateFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async previewTemplate({
    params,
    body,
  }: {
    params: PreviewTemplateParams;
    body: PreviewTemplateRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, PreviewTemplateParams),
      this.validator.validateOrReject(body, PreviewTemplateRequest),
    ]);

    let requestPath = '/template/{template_id}/preview';

    const paramsKeys = Object.keys(params) as (keyof PreviewTemplateParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(result, FileResponse);

    return response;
  }

  async downloadTemplateFile({
    query = {},
    params,
  }: {
    query?: DownloadTemplateFileQuery;
    params: DownloadTemplateFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, DownloadTemplateFileQuery),
      this.validator.validateOrReject(params, DownloadTemplateFileParams),
    ]);

    let requestPath = '/template/{template_id}/download';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DownloadTemplateFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: true,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async uploadTemplateFile({
    params,
    body,
  }: {
    params: UploadTemplateFileParams;
    body: UploadTemplateRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UploadTemplateFileParams),
      this.validator.validateOrReject(body, UploadTemplateRequest),
    ]);

    let requestPath = '/template/{template_id}/upload';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UploadTemplateFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      UploadTemplateResponse,
    );

    return response;
  }

  async getTemplate({ params }: { params: GetTemplateParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetTemplateParams),
    ]);

    let requestPath = '/template/{template_id}';

    const paramsKeys = Object.keys(params) as (keyof GetTemplateParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      InfoTemplateResponse,
    );

    return response;
  }

  async getTemplateExampleFile() {
    let requestPath = '/template/example';

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async getTemplateHistory({ params }: { params: GetTemplateHistoryParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetTemplateHistoryParams),
    ]);

    let requestPath = '/template/{template_id}/history';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetTemplateHistoryParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      TemplateHistoryResponse,
    );

    return response;
  }

  async getTemplateList({ body }: { body: CompanyTemplatesRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CompanyTemplatesRequest),
    ]);

    let requestPath = '/template/list';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      TemplatesListResponse,
    );

    return response;
  }
}
